/**
 * 路由表
 * ---------------------------------------------------------------------------
 * 和 nav-config.ts 一一对应：那边定义"侧栏怎么显示"，这边定义"渲染哪个组件"。
 * 加页面时两个文件都要改，这是刻意的——防止出现侧栏点不到或点了空白的页面。
 *
 * 应用有两套外壳：
 *   /...    老板端 AppShell（侧栏 + 顶栏，桌面为主，之后由 Electron 封装）
 *   /m/...  员工手机端 MobileShell（底部四个 tab，产品文档 §8.10）
 * 两套各有自己的页面清单和权限解析函数，共用登录态、http 层和 QueryClient。
 *
 * 为什么这里全用 lazy：
 * 两套外壳打在一个包里之后，发型师用手机打开员工端会连整个老板端一起下载
 * （报表、图表、库存、营销都在里面）。门店的网络本来就不稳，这部分流量是白费的。
 * 按路由切开之后，员工端只下载自己那几页，老板端也不会带上员工端。
 * 登录页保持同步导入——它是未登录时的首屏，再多一次往返就是白屏时间。
 */
import { Suspense, lazy } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { ErrorBoundary } from "@/app/error-boundary";
import { findMobileNavItem } from "@/app/mobile/mobile-nav-config";
import { RequireAuth } from "@/app/require-auth";
import { RequireFeature } from "@/app/require-feature";
import { HomeRedirect } from "@/app/home-redirect";
import { LoadingBlock } from "@/components/ui/empty-state";
import { LoginPage } from "@/features/auth/login-page";

/* 外壳 */
const AppShell = lazy(() =>
  import("@/app/layout/app-shell").then((m) => ({ default: m.AppShell })),
);
const MobileShell = lazy(() =>
  import("@/app/mobile/mobile-shell").then((m) => ({ default: m.MobileShell })),
);

/* 员工手机端 */
const EmployeeTodayPage = lazy(() =>
  import("@/features/employee/today-page").then((m) => ({
    default: m.EmployeeTodayPage,
  })),
);
const EmployeeCustomersPage = lazy(() =>
  import("@/features/employee/customers-page").then((m) => ({
    default: m.EmployeeCustomersPage,
  })),
);
const EmployeeEarningsPage = lazy(() =>
  import("@/features/employee/earnings-page").then((m) => ({
    default: m.EmployeeEarningsPage,
  })),
);
const EmployeeProfilePage = lazy(() =>
  import("@/features/employee/profile-page").then((m) => ({
    default: m.EmployeeProfilePage,
  })),
);

/* 老板端 */
const DashboardPage = lazy(() =>
  import("@/features/dashboard/dashboard-page").then((m) => ({
    default: m.DashboardPage,
  })),
);
const CheckoutPage = lazy(() =>
  import("@/features/checkout/checkout-page").then((m) => ({
    default: m.CheckoutPage,
  })),
);
const OrdersPage = lazy(() =>
  import("@/features/orders/orders-page").then((m) => ({
    default: m.OrdersPage,
  })),
);
const MembersPage = lazy(() =>
  import("@/features/members/members-page").then((m) => ({
    default: m.MembersPage,
  })),
);
const AppointmentsPage = lazy(() =>
  import("@/features/appointments/appointments-page").then((m) => ({
    default: m.AppointmentsPage,
  })),
);
const StaffPage = lazy(() =>
  import("@/features/staff/staff-page").then((m) => ({ default: m.StaffPage })),
);
const CommissionsPage = lazy(() =>
  import("@/features/commissions/commissions-page").then((m) => ({
    default: m.CommissionsPage,
  })),
);
const CatalogPage = lazy(() =>
  import("@/features/catalog/catalog-page").then((m) => ({
    default: m.CatalogPage,
  })),
);
const MarketingPage = lazy(() =>
  import("@/features/marketing/marketing-page").then((m) => ({
    default: m.MarketingPage,
  })),
);
const InventoryPage = lazy(() =>
  import("@/features/inventory/inventory-page").then((m) => ({
    default: m.InventoryPage,
  })),
);
const FinancePage = lazy(() =>
  import("@/features/finance/finance-page").then((m) => ({
    default: m.FinancePage,
  })),
);
const ReportsPage = lazy(() =>
  import("@/features/reports/reports-page").then((m) => ({
    default: m.ReportsPage,
  })),
);
const SupportTicketsPage = lazy(() =>
  import("@/features/support/support-tickets-page").then((m) => ({
    default: m.SupportTicketsPage,
  })),
);
const SupportAccessPage = lazy(() =>
  import("@/features/settings/support-access-page").then((m) => ({
    default: m.SupportAccessPage,
  })),
);
const BillingPage = lazy(() =>
  import("@/features/settings/billing-page").then((m) => ({
    default: m.BillingPage,
  })),
);
const SettingsPage = lazy(() =>
  import("@/features/settings/settings-page").then((m) => ({
    default: m.SettingsPage,
  })),
);
const DevicesPage = lazy(() =>
  import("@/features/devices/devices-page").then((m) => ({
    default: m.DevicesPage,
  })),
);
const DeploymentPage = lazy(() =>
  import("@/features/deployment/deployment-page").then((m) => ({
    default: m.DeploymentPage,
  })),
);
const NotFoundPage = lazy(() =>
  import("@/features/misc/not-found-page").then((m) => ({
    default: m.NotFoundPage,
  })),
);
const FirstLoginPasswordPage = lazy(() =>
  import("@/features/auth/first-login-password-page").then((m) => ({
    default: m.FirstLoginPasswordPage,
  })),
);

export function AppRoutes() {
  const location = useLocation();

  return (
    // key 绑路由：一个页面出错后，用户切到别的页面应该能正常用，
    // 而不是被上一页的错误一直卡住（错误边界本身不会自己复位）。
    //
    // 用 `resetKey` 而不是 `key`：`key` 变化会把整棵子树卸载重建，
    // 侧栏跟着重新挂载、滚动回到顶部——点最下面几个菜单时会"往上顶"一下。
    //
    // 错误边界要包在 Suspense 外面：分块加载失败是从 Suspense 里抛出来的，
    // 包在里面接不住。
    <ErrorBoundary resetKey={location.pathname}>
      {/* 分包之后切页面会有一次网络往返，必须给占位，否则门店会看到闪一下的空白 */}
      <Suspense fallback={<LoadingBlock label="正在打开" />}>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route
            path="/change-password"
            element={
              <RequireAuth>
                <FirstLoginPasswordPage />
              </RequireAuth>
            }
          />

          {/*
          打开应用先按权限决定进哪一端。放在外壳之外是有意的：
          只有 staff.self.read 的员工不该先把老板端外壳挂起来（它会立刻请求
          功能开关和全店预约，两个都会 403），再被重定向走。
        */}
          <Route
            path="/"
            element={
              <RequireAuth>
                <HomeRedirect />
              </RequireAuth>
            }
          />

          {/* 员工手机端 */}
          <Route
            element={
              <RequireAuth resolveNavItem={findMobileNavItem}>
                <MobileShell />
              </RequireAuth>
            }
          >
            <Route path="/m" element={<Navigate to="/m/today" replace />} />
            <Route path="/m/today" element={<EmployeeTodayPage />} />
            <Route path="/m/customers" element={<EmployeeCustomersPage />} />
            <Route path="/m/earnings" element={<EmployeeEarningsPage />} />
            <Route path="/m/profile" element={<EmployeeProfilePage />} />
            {/*
            必须在这一组里兜住 /m/*，否则输错的员工端地址会落到下面老板端那一组的
            通配路由上，把发型师直接扔进老板端侧栏——他在那儿只会看到没有权限。
          */}
            <Route path="/m/*" element={<NotFoundPage employee />} />
          </Route>

          {/*
            管理端这一组带 `console`：员工角色进不来，会被送去手机端。
            改密页在这一组**之外**（上面单独一条路由），所以任何账号
            在任何时候都改得了自己的密码。
          */}
          <Route
            element={
              <RequireAuth console>
                <AppShell />
              </RequireAuth>
            }
          >
            {/* 每天使用 */}
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/checkout" element={<CheckoutPage />} />
            <Route path="/orders" element={<OrdersPage />} />
            <Route path="/members" element={<MembersPage />} />
            <Route
              path="/appointments"
              element={
                <RequireFeature feature="appointments">
                  <AppointmentsPage />
                </RequireFeature>
              }
            />

            {/* 经营管理 */}
            <Route path="/staff" element={<StaffPage />} />
            <Route path="/commissions" element={<CommissionsPage />} />
            <Route path="/catalog" element={<CatalogPage />} />
            <Route
              path="/marketing"
              element={
                <RequireFeature feature="marketing">
                  <MarketingPage />
                </RequireFeature>
              }
            />
            <Route
              path="/inventory"
              element={
                <RequireFeature feature="inventory">
                  <InventoryPage />
                </RequireFeature>
              }
            />
            <Route path="/finance" element={<FinancePage />} />
            <Route path="/reports" element={<ReportsPage />} />

            {/* 系统 */}
            <Route path="/support" element={<SupportTicketsPage />} />
            <Route path="/support-access" element={<SupportAccessPage />} />
            <Route path="/billing" element={<BillingPage />} />
            {/*
              **旧地址一律保留成重定向。** 这一页改过两次名：
              先是套餐页 /plans 并进来，2026-08-13 又从"功能中心" /features
              改叫"订阅与账单" /billing（能力开关搬去门店设置之后，
              原来那个名字和路径都名不副实了，/features 还和后端的功能开关接口撞名）。
              顶栏版本标签、只读横幅、商户自己存的书签都指过旧地址，直接删会变 404。
            */}
            <Route path="/features" element={<Navigate to="/billing" replace />} />
            <Route path="/plans" element={<Navigate to="/billing" replace />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/devices" element={<DevicesPage />} />
            <Route path="/deployment" element={<DeploymentPage />} />

            <Route path="*" element={<NotFoundPage />} />
          </Route>
        </Routes>
      </Suspense>
    </ErrorBoundary>
  );
}
